import { useEffect, useMemo, useState } from 'react';

export function useBulkSelection<T extends string | number>(currentIds: T[]) {
  const [selectedIds, setSelectedIds] = useState<T[]>([]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = prev.filter((id) => currentIds.includes(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }

      return next;
    });
  }, [currentIds]);

  const allSelected = useMemo(
    () => currentIds.length > 0 && currentIds.every((id) => selectedIds.includes(id)),
    [currentIds, selectedIds]
  );

  const isSelected = (id: T) => selectedIds.includes(id);

  const toggleOne = (id: T, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }

      return prev.filter((item) => item !== id);
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? [...currentIds] : []);
  };

  const clearSelection = () => setSelectedIds([]);

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    allSelected,
    hasSelection: selectedIds.length > 0,
    isSelected,
    toggleOne,
    toggleAll,
    clearSelection,
  };
}
